/**
 * Watch Period Repository
 * 
 * Data access layer for watch keeping schedules
 * Manages watch periods and officer assignments
 * 
 * @module repositories/WatchPeriodRepository
 */

import { BaseRepository, QueryOptions, QueryResult } from '../BaseRepository';
import { db } from '../db';
import type { WatchPeriod, WatchType } from '../../../types/logbook';
import { createLogger } from '../../utils/logger';

const logger = createLogger('WatchPeriodRepository');

export class WatchPeriodRepository extends BaseRepository<WatchPeriod> {
  constructor() {
    super(db.logbook_watch_periods, 'logbook_watch_periods');
  }

  // ============================================================================
  // BASIC QUERIES
  // ============================================================================

  /**
   * Find watch periods by ship
   */
  async findByShip(vesselId: string, options?: QueryOptions): Promise<QueryResult<WatchPeriod>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, vesselId }
    });
  }

  /**
   * Find watch periods by type
   */
  async findByType(vesselId: string, watchType: WatchType): Promise<WatchPeriod[]> {
    return await this.table
      .where('[vesselId+watchType]')
      .equals([vesselId, watchType])
      .toArray();
  }

  /**
   * Find watch periods by officer
   */
  async findByOfficer(officerId: string): Promise<WatchPeriod[]> {
    return await this.table
      .where('officerId')
      .equals(officerId)
      .toArray();
  }

  /**
   * Get active watch periods for a ship
   */
  async getActive(vesselId: string, date?: Date): Promise<WatchPeriod[]> {
    const targetDate = date || new Date();

    return await this.table
      .where('vesselId')
      .equals(vesselId)
      .and(period => {
        if (!period.isActive) return false;
        
        const effectiveFrom = new Date(period.effectiveFrom);
        if (targetDate < effectiveFrom) return false;
        
        if (period.effectiveTo) {
          const effectiveTo = new Date(period.effectiveTo);
          if (targetDate > effectiveTo) return false;
        }
        
        return true;
      })
      .toArray();
  }

  // ============================================================================
  // ADVANCED QUERIES
  // ============================================================================

  /**
   * Get current watch at specific time
   */
  async getCurrentWatch(
    vesselId: string,
    watchType: WatchType,
    dateTime?: Date
  ): Promise<WatchPeriod | undefined> {
    const now = dateTime || new Date();
    const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    const activeWatches = await this.getActive(vesselId, now);
    
    // Find watch that covers current time
    for (const watch of activeWatches) {
      if (watch.watchType !== watchType) continue;

      // Compare times (simple string comparison works for HH:MM format)
      if (this.isTimeInRange(time, watch.startTime, watch.endTime)) {
        return watch;
      }
    }

    return undefined;
  }

  /**
   * Get watch schedule for a date
   */
  async getScheduleForDate(
    vesselId: string,
    date: Date
  ): Promise<{ deck: WatchPeriod[]; engine: WatchPeriod[] }> {
    const activeWatches = await this.getActive(vesselId, date);

    return {
      deck: activeWatches.filter(w => w.watchType === 'deck'),
      engine: activeWatches.filter(w => w.watchType === 'engine')
    };
  }

  /**
   * Find watch periods with scheduling conflicts
   */
  async findConflicts(vesselId: string): Promise<Array<{ period1: WatchPeriod; period2: WatchPeriod }>> {
    const conflicts: Array<{ period1: WatchPeriod; period2: WatchPeriod }> = [];
    const activeWatches = await this.getActive(vesselId);

    for (let i = 0; i < activeWatches.length; i++) {
      for (let j = i + 1; j < activeWatches.length; j++) {
        const watch1 = activeWatches[i];
        const watch2 = activeWatches[j];

        // Check if same officer is assigned to overlapping watches
        if (watch1.officerId === watch2.officerId &&
            this.doTimesOverlap(watch1.startTime, watch1.endTime, watch2.startTime, watch2.endTime)) {
          conflicts.push({ period1: watch1, period2: watch2 });
        }
      }
    }

    return conflicts;
  }

  /**
   * Get officer workload (hours per week)
   */
  async getOfficerWorkload(officerId: string): Promise<number> {
    const watches = await this.table
      .where('officerId')
      .equals(officerId)
      .and(w => w.isActive)
      .toArray();

    // Calculate total hours per week
    let totalHours = 0;
    for (const watch of watches) {
      // Assuming each watch repeats daily
      totalHours += watch.durationHours * 7;
    }

    return totalHours;
  }

  // ============================================================================
  // WATCH OPERATIONS
  // ============================================================================

  /**
   * Create watch period with validation
   */
  async createWatch(data: Omit<WatchPeriod, 'id' | 'createdAt' | 'updatedAt'>): Promise<WatchPeriod> {
    // Validate duration
    if (data.durationHours <= 0 || data.durationHours > 12) {
      throw new Error('Watch duration must be between 0 and 12 hours');
    }

    // Check for conflicts if officer is assigned
    if (data.officerId && data.vesselId) {
      const conflicts = await this.findConflicts(data.vesselId);
      if (conflicts.length > 0) {
        logger.warn('Watch schedule conflict detected', { officerId: data.officerId });
      }
    }

    const now = new Date();
    const watch: WatchPeriod = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now
    };

    await this.table.add(watch);
    return watch;
  }

  /**
   * Update watch period
   */
  async updateWatch(id: string, data: Partial<WatchPeriod>): Promise<WatchPeriod> {
    const existing = await this.findById(id);
    if (!existing) throw new Error('Watch period not found');

    // Validate duration if provided
    if (data.durationHours !== undefined) {
      if (data.durationHours <= 0 || data.durationHours > 12) {
        throw new Error('Watch duration must be between 0 and 12 hours');
      }
    }

    return await this.update(id, data);
  }

  /**
   * Assign officer to watch
   */
  async assignOfficer(
    watchId: string,
    officerId: string,
    officerName: string,
    officerRank: string
  ): Promise<WatchPeriod> {
    return await this.update(watchId, {
      officerId,
      officerName,
      officerRank
    });
  }

  /**
   * Activate watch period
   */
  async activate(id: string): Promise<WatchPeriod> {
    return await this.update(id, { isActive: true });
  }

  /**
   * Deactivate watch period
   */
  async deactivate(id: string, effectiveTo?: Date): Promise<WatchPeriod> {
    return await this.update(id, {
      isActive: false,
      effectiveTo: effectiveTo || new Date()
    });
  }

  /**
   * Clone watch period
   */
  async clone(id: string): Promise<WatchPeriod> {
    const original = await this.findById(id);
    if (!original) throw new Error('Watch period not found');

    const cloned: Omit<WatchPeriod, 'id' | 'createdAt' | 'updatedAt'> = {
      organizationId: original.organizationId,
      vesselId: original.vesselId,
      watchName: `${original.watchName} (Copy)`,
      watchType: original.watchType,
      startTime: original.startTime,
      endTime: original.endTime,
      durationHours: original.durationHours,
      officerId: undefined, // Don't copy officer assignment
      officerName: undefined,
      officerRank: undefined,
      ratingIds: original.ratingIds,
      schedule: original.schedule,
      effectiveFrom: new Date(),
      effectiveTo: undefined,
      isActive: false, // Clone is inactive by default
      notes: original.notes
    };

    return await this.createWatch(cloned);
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Create standard watch schedule (4 on / 8 off)
   */
  async createStandardSchedule(
    organizationId: string,
    vesselId: string,
    watchType: WatchType
  ): Promise<WatchPeriod[]> {
    const now = new Date();
    const watches: WatchPeriod[] = [];

    // Standard maritime watch schedule
    const scheduleData = [
      { name: 'First Watch', start: '20:00', end: '00:00', hours: 4 },
      { name: 'Middle Watch', start: '00:00', end: '04:00', hours: 4 },
      { name: 'Morning Watch', start: '04:00', end: '08:00', hours: 4 },
      { name: 'Forenoon Watch', start: '08:00', end: '12:00', hours: 4 },
      { name: 'Afternoon Watch', start: '12:00', end: '16:00', hours: 4 },
      { name: 'Dog Watch (First)', start: '16:00', end: '18:00', hours: 2 },
      { name: 'Dog Watch (Last)', start: '18:00', end: '20:00', hours: 2 }
    ];

    for (const schedule of scheduleData) {
      const watch: WatchPeriod = {
        id: this.generateId(),
        organizationId,
        vesselId: vesselId,

        watchName: schedule.name,
        watchType,
        startTime: schedule.start,
        endTime: schedule.end,
        durationHours: schedule.hours,
        schedule: '4_on_8_off',
        effectiveFrom: now,
        isActive: true,
        createdAt: now,
        updatedAt: now
      };

      await this.table.add(watch);
      watches.push(watch);
    }

    return watches;
  }

  /**
   * Bulk deactivate watches
   */
  async bulkDeactivate(watchIds: string[], effectiveTo?: Date): Promise<number> {
    let deactivated = 0;

    for (const id of watchIds) {
      try {
        await this.deactivate(id, effectiveTo);
        deactivated++;
      } catch (error) {
        logger.error('Failed to deactivate watch', { id, error });
      }
    }

    return deactivated;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if time is in range (handles midnight crossing)
   */
  private isTimeInRange(time: string, startTime: string, endTime: string): boolean {
    if (startTime <= endTime) {
      // Normal range (e.g., 08:00 to 12:00)
      return time >= startTime && time < endTime;
    } else {
      // Crosses midnight (e.g., 20:00 to 04:00)
      return time >= startTime || time < endTime;
    }
  }

  /**
   * Check if two time ranges overlap
   */
  private doTimesOverlap(
    start1: string,
    end1: string,
    start2: string,
    end2: string
  ): boolean {
    // Convert to minutes for easier comparison
    const toMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const s1 = toMinutes(start1);
    let e1 = toMinutes(end1);
    const s2 = toMinutes(start2);
    let e2 = toMinutes(end2);

    // Handle midnight crossing
    if (e1 < s1) e1 += 24 * 60;
    if (e2 < s2) e2 += 24 * 60;

    // Check for overlap
    return (s1 < e2 && e1 > s2) || (s2 < e1 && e2 > s1);
  }
}

export const watchPeriodRepository = new WatchPeriodRepository();




